import { useState } from "react";
import { Card, CardProps } from "@imminently/react/ui/core/card";
import { Text } from "@imminently/react/ui/core/text";
import { 
  Box, 
  Stack, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button,
  FormControl,
  Select,
  MenuItem,
  OutlinedInput,
  Alert
} from "@mui/material";

import { HeartIcon } from "@/assets/icons";
import { formatDuration, getTimeFromNow } from "@/global";
import { PatientEncounter } from "@/resources/types";
import { Theme, css } from "@/theme";
import { getTriageFromSettings, getTriageLevelColours } from "@/util/triage";
import { useSettingsContext } from "@/pages/settings/SettingsProvider";
import { encounterService } from "@/services";

const styles = {
  card: (theme: Theme) => css`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 0.5rem 1rem;
    min-width: 12rem;
    box-shadow: none;
    cursor: pointer;
    border-radius: 0.5rem;
    gap: 0.5rem;
    transition: all 0.2s ease;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    &[data-due="true"] {
      background: ${theme.palette.common.red[100]};
      border: 1px solid ${theme.palette.common.red[300]};
    }

    &[data-due="false"] {
      background: ${theme.palette.common.green[100]};
      border: 1px solid ${theme.palette.common.green[300]};
    }
  `,
};

type PatientNextObservationProps = CardProps & {
  encounter: PatientEncounter;
  onEncounterChange?: (path: string, value: any) => void;
};

const PatientNextObservation = ({ encounter, onEncounterChange, ...props }: PatientNextObservationProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedObservationLevel, setSelectedObservationLevel] = useState<string>((encounter as any).observationLevel || "");
  const [isSaving, setIsSaving] = useState(false);
  const { settings } = useSettingsContext();
  const { mutateAsync: updateEncounter } = encounterService.useUpdate();

  const nextObsDue = encounter.nextObsDue;
  
  let title = "-";
  let due = false;
  let colours = null;

  if (nextObsDue) {
    const duration = getTimeFromNow(nextObsDue);
    const minsDue = duration.asMinutes();
    
    title = minsDue <= 0 ? "Now" : formatDuration(duration);
    due = minsDue <= 0;
    
    // Get triage level colors if available
    const triage = getTriageFromSettings(encounter.atsScore, settings);
    if (triage) {
      colours = getTriageLevelColours(triage.colourPalette);
    }
  }

  // Get observation levels from settings
  const observationLevels = (settings as any)?.encounter?.observations?.levels || [];
  const hasObservationLevels = observationLevels.length > 0;

  const handleClick = () => {
    setSelectedObservationLevel((encounter as any).observationLevel || "");
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedObservationLevel((encounter as any).observationLevel || "");
  };

  const handleSave = async () => {
    if (!onEncounterChange) return;
    
    setIsSaving(true);
    try {
      // Update the encounter with the new observation level
      onEncounterChange('observationLevel', selectedObservationLevel || null);
      setModalOpen(false);
    } catch (error) {
      console.error('Failed to update observation level:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Card 
        {...props} 
        css={styles.card} 
        onClick={handleClick} 
        data-due={due}
        sx={{
          backgroundColor: colours?.background || (due ? 'error.100' : 'success.100'),
          borderColor: colours?.border || (due ? 'error.300' : 'success.300'),
        }}
      >
        <HeartIcon />
        <Stack direction="row" sx={{ alignItems: "baseline" }} spacing={1}>
          <Text variant="body2" sx={{ color: colours?.text }}>
            Next obs
          </Text>
          <Text
            variant="h6"
            color={due ? "error.main" : undefined}
            sx={{ color: colours?.text }}
          >
            {title}
          </Text>
         
        </Stack>
      </Card>

      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>Configure Observation Level</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {!hasObservationLevels ? (
              <Alert severity="warning">
                No observation levels are configured in settings. The default observation level will be used.
              </Alert>
            ) : (
              <>
                <Text variant="body1">
                  Select the observation level for this patient:
                </Text>
                <FormControl fullWidth>
                  <Select
                    value={selectedObservationLevel}
                    onChange={(e) => setSelectedObservationLevel(e.target.value)}
                    input={<OutlinedInput />}
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>No observation level (use default)</em>
                    </MenuItem>
                    {observationLevels.map((level: any) => (
                      <MenuItem key={level.id} value={level.id}>
                        {level.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} disabled={isSaving}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PatientNextObservation;
